import { formatDate } from '@/utils/formatDateToTime';
// The shared string enum. NOT the local numeric ONBOARD_STATUS map below —
// that one maps these names to register-form stage indexes.
import ONBOARDING_STATUS from '@/constants/ONBOARDING_STATUS';
import {
  startMixPanel,
  trackEvent,
  persistProfileSnapshot,
  validateCachedIdentityAgainstAuth,
} from '@/utils/mixpanel';
import { auth0 } from '@/utils/auth0';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';
import dayjs from 'dayjs';
import { CAREER_FIELD_KEYS } from '@/constants/profile/careerFields';
import { identifyOneSignalMember } from '@/utils/notification';
import { oneSignalExternalId } from '@/utils/onesignal-external-id';
import { refreshTokenIfMemberLinkChanged } from '@/utils/member-link-token-refresh';
import { othersLast } from '@/utils/others-last';
import {
  getOnboardingStatusFromStorage,
  saveOnboardingStatusToStorage,
} from '@/utils/onboarding-status-storage';
import { setCrispMetadata } from '@/utils/crisp';
import { verifyPersonalIdAt } from '@/utils/verify-personal-id-request';

export const REGISTER_STAGE = {
  FILL_BIO: 0,
  FILL_CONTACT: 1,
  FILL_LIFESTAGE: 2,
  VERIFY_OTP: 3,
  OTP_VERIFIED: 4,
  SET_PASSWORD: 5,
  COMPLETED: 6,
};

export const ONBOARD_STATUS = {
  NOT_STARTED: 0,
  STAGE_1_BIO_COMPLETED: 1,
  STAGE_2_CONTACT_COMPLETED: 2,
  STAGE_2_CONTACT_COMPLETED_REGISTRATION: 2,
  STAGE_3_LIFESTAGE_COMPLETED: 3,
};

const INITIAL_REGISTER_STATE = {
  stage: REGISTER_STAGE.FILL_BIO,
  familyName: '',
  givenName: '',
  preferredName: '',
  gender: '',
  dateOfBirth: '',
  partialNric: '',
  mobileNumber: '',
  email: '',
  stageOfLife: null,
  schoolName: null,
  maritalStatus: null,
  haveChildren: null,
  numberOfChildren: null,
  dirtied: new Set(),
  showMobileOtp: false,
  showEmailOtp: false,
  hideRegisterHeader: false,
  isMobileNumberVerified: false,
  isEmailVerified: false,
  showMobileNumberIfVerified: '',
  country: '',
  nationalityId: '',
};

const INITIAL_STATE = {
  loading: false,
  register: INITIAL_REGISTER_STATE,
  errorMessages: {},
  hasRegistered: '',
  schools: null,
  launchBadgeEligibility: null,
  hasSeenLdrsCal: false,
  isLeader: false,
  access: null, // Array of access IDs (e.g., [700] for Absolutely access)
  // Member split, phase 4b: signed in with an account the CG leader has not linked yet.
  hasNoMember: false,
  // The login's own id, kept only while it has no member (OneSignal external id).
  appUserId: null,
};
const state = () => INITIAL_STATE;

// The profile request in the air, if any. `fetchProfile` is asked for by the
// app shell at boot (Next Steps key hydration is keyed on the profile id) and
// by whichever layout mounts first, within the same tick; without this each
// asker sent its own GET /v2/profile.
let profileFetch = null;

const mutations = {
  setProfileState(state, newProfile) {
    for (const key in newProfile) state[key] = newProfile[key];
  },
  setAccess(state, access) {
    state.access = access;
  },
  setLoading(state, isLoading) {
    state.loading = isLoading;
  },
  setMinistryStatus(state, ministryStatus) {
    state.ministryStatus = ministryStatus;
  },
  setHasSeenLdrsCal(state, hasSeenLdrsCal) {
    state.hasSeenLdrsCal = hasSeenLdrsCal;
  },
  setHasSeenUpdates(state, hasSeenUpdates) {
    state.hasSeenUpdates = hasSeenUpdates;
  },
  setIsLeader(state, isLeader) {
    state.isLeader = isLeader;
  },
  setShowingProfileModal(state, showingProfileModal) {
    state.showingProfileModal = showingProfileModal;
  },
  setDob(state, dateOfBirth) {
    state.dateOfBirth = dateOfBirth;
  },
  setUin(state, uin) {
    state.uin = uin;
  },
  setRegisterFormState(state, { key, value }) {
    state.register[key] = value;
  },
  setOnboardStatus(state, { key, value }) {
    state[key] = value;
  },
  setRegisterFormStateDirtied(state, { key }) {
    state.register.dirtied.add(key);
  },
  setErrorMessages(state, { key, messages }) {
    state.errorMessages = { ...state.errorMessages, [key]: messages };
  },
  setSchools(state, schools) {
    state.schools = schools;
  },
  setLaunchBadgeEligibility(state, eligibility) {
    state.launchBadgeEligibility = eligibility;
  },
};

const actions = {
  async fetchProfile({ dispatch, state }) {
    // Will not call backend api if profile already set
    if (state.givenName) {
      // Set Crisp metadata if profile is already loaded (works in both browser and mobile app)
      setCrispMetadata({
        email: state.email,
        preferredName: state.preferredName,
        givenName: state.givenName,
        id: state.id,
        cgName: state.cgName,
        role: state.role,
      }).catch(error => {
        console.warn('[Profile] Failed to set Crisp metadata:', error);
      });
      return;
    }
    // One request however many callers ask while it is in the air; every one
    // of them gets its outcome, and the next ask after it lands is a new one.
    if (!profileFetch) {
      profileFetch = dispatch('forceFetchProfile').finally(() => {
        profileFetch = null;
      });
    }
    await profileFetch;
  },

  async forceFetchProfile({ commit, dispatch, rootState }) {
    commit('setLoading', true);

    try {
      const isNative = isPlatform('ios') || isPlatform('android');
      const isNativeBrowser = isPlatform('mobileweb');
      commit('setLoading', true);
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(`${process.env.VUE_APP_API_URL}/v2/profile`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (status !== 200) throw new Error('Failed in setting profile');

      // Filter out childrenBirthYears if it exists in the response (being removed from backend)
      // Keep numberOfChildren as-is (backend uses numberOfChildren)
      const { childrenBirthYears, ...profileData } = data;
      // numberOfChildren is already in profileData, no mapping needed

      Object.entries(profileData).forEach(([key, value]) => {
        dispatch('inputRegisterForm', { key: key, value: value });
      });

      if (profileData.isMobileNumberVerified)
        dispatch('inputRegisterForm', {
          key: 'showMobileNumberIfVerified',
          value: profileData.mobileNumber,
        });

      // strip time and timezone context for date of birth so that it
      // always display the same date no matter the timezone
      if (profileData.dateOfBirth) {
        const dateOfBirth = new Date(profileData.dateOfBirth);
        const date = new Date(
          dateOfBirth.getFullYear(),
          dateOfBirth.getUTCMonth(),
          dateOfBirth.getUTCDate(),
        );

        profileData.dateOfBirth = formatDate(date, false);
      }

      // Only a not-yet-linked account's profile carries it, so a member's clears it.
      // Giving per login: an unverified secondary login gets its partial NRIC and
      // personal id as null, with `personalIdLocked` saying why. Absent (an older
      // API) is false, so the fields behave as before.
      commit('setProfileState', {
        ...profileData,
        hasNoMember: data.hasNoMember === true,
        // Set every time, so a member's profile clears a previous unlinked login's id.
        appUserId: data.hasNoMember === true ? (data.appUserId ?? null) : null,
        personalIdLocked: data.personalIdLocked === true,
      });

      // Explicitly set access if it exists in the response
      if (data.access !== undefined) {
        commit('setAccess', data.access);
      }

      commit('setLoading', false);

      // OneSignal tags are per member, shared by every login of it, so they carry the
      // member's own email (`email` is this login's). A login with no member, or an
      // older API without `memberEmail`, tags the login's email as before.
      const userProps = {
        memberEmail: data.memberEmail ?? data.email,
        memberName: data.givenName,
        memberCG: data.cgName,
        memberRole: data.role,
        memberZone: data.zoneName,
        memberDemography: data?.demography,
        memberStageOfLife: data?.stageOfLife,
        memberServiceId: data?.serviceId,
        isLeader: data?.isLeader,
        // OneSignal tag values are strings. 'false' only for a login with no member yet.
        linked: data.hasNoMember === true ? 'false' : 'true',
      };
      if (isNative && !isNativeBrowser) {
        identifyOneSignalMember(oneSignalExternalId(data), userProps);
      }

      // giving-api reads the member only from the token's member_id claim, so when this
      // login's member link moved (a leader linked it, or an undo moved it back) the token
      // on the device names the old one. Fetch a fresh token once per change; never throws.
      await refreshTokenIfMemberLinkChanged({
        profile: data,
        authSub: auth0?.user?.value?.sub ?? null,
        onToken: token => commit('user/setToken', token, { root: true }),
      });

      // Set Datadog RUM user context
      const { setDatadogRumUser } = await import('@/utils/datadog');
      setDatadogRumUser({
        id: data.id,
      });

      // Tag the Capgo device with the member, so a stuck install can be found in
      // the console at all. Without it every device row is an anonymous UUID.
      // Fire-and-forget and deliberately not awaited: this is diagnostics, and
      // the profile fetch is on the boot path.
      import('@/utils/ota')
        .then(({ setOtaCustomId }) => setOtaCustomId(data.id))
        .catch(() => {
          // Never let a diagnostics tag break sign-in.
        });

      // Fix C — before we overwrite the snapshot, check whether the cached
      // identity belonged to a different Auth0 user. If so, wipe analytics
      // state so events from the pre-Auth0 identify window (attributed to
      // the previous user on this device) don't leak across users.
      const currentAuthSub = auth0?.user?.value?.sub ?? null;
      validateCachedIdentityAgainstAuth(currentAuthSub);

      // Fix C — persist a snapshot so the next cold start can identify
      // Mixpanel/OneSignal even if Auth0 or /v2/profile fails. `authSub`
      // lets subsequent launches detect a device-owner change.
      persistProfileSnapshot({
        id: data.id,
        cgName: data.cgName,
        zoneName: data.zoneName,
        role: data.role,
        dateOfBirth: data.dateOfBirth,
        stageOfLife: data.stageOfLife,
        educationLevel: data.educationLevel,
        demography: data.demography,
        isLeader: data.isLeader,
        authSub: currentAuthSub,
      });

      startMixPanel(
        data.id,
        data.cgName,
        data.zoneName,
        data.role,
        data.dateOfBirth,
        data.stageOfLife,
        data.educationLevel, // Education level name (mapped from levelRankId in backend)
        data.demography, // Demographics based on CG zone
        data.isLeader, // Active row in member_leadership_role
      );

      // Fix D — explicit Login boundary event so we can measure identify rate
      // vs app-open rate in Mixpanel going forward.
      try {
        trackEvent('Login', {
          memberId: data.id,
          method: 'auth0',
          source: 'profile-fetch',
        });
        console.log('[Fix-D Login] trackEvent fired for member', data.id);
      } catch (loginEventError) {
        console.warn('[Fix-D Login] Failed to fire Login event:', loginEventError);
      }

      // Set Crisp chat metadata (works in both browser and mobile app)
      setCrispMetadata({
        email: data.email,
        preferredName: data.preferredName,
        givenName: data.givenName,
        id: data.id,
        cgName: data.cgName,
        role: data.role,
      }).catch(error => {
        console.warn('[Profile] Failed to set Crisp metadata:', error);
      });
    } catch (e) {
      dispatch('toast/error', 'Failed to load profile.', { root: true });
      console.error(e);
      throw e;
    } finally {
      commit('setLoading', false);
    }
  },

  async updateProfile({ commit, dispatch, rootState }, updatedProfile) {
    commit('setLoading', true);
    // Split fullName into an array
    // const nameParts = updatedProfile.fullName.split(" ");
    // updatedProfile.familyName = nameParts[0];
    updatedProfile.givenName = updatedProfile.fullName;
    try {
      const accessToken = rootState.user.token;
      const response = await axios.request(process.env.VUE_APP_API_URL + '/v2/profile', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        data: updatedProfile,
      });
      const data = response.data;
      dispatch('forceFetchProfile');
      return data;
      // A failure is not toasted here: every caller says what failed
      // (profileSaveErrorMessage: the API's text, or its own fallback; a clash offers
      // support; a locked personal id saved the rest), so one failure, one message.
    } finally {
      commit('setLoading', false);
    }
  },

  // `force` skips the loaded-once guard. Forms pass it: the app stays alive for
  // days, so a value loaded at launch can be stale by the time a form opens.
  async fetchMinistryStatus({ state, commit, dispatch, rootState }, { force = false } = {}) {
    if (state.ministryStatus && !force) return;

    try {
      const accessToken = rootState.user.token;
      const { data } = await axios.get(process.env.VUE_APP_API_URL + '/profile/ministry-status', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      commit('setMinistryStatus', data);
    } catch (error) {
      dispatch('toast/error', 'Error getting ministry status', { root: true });
    }
  },

  /**
   * My QR, giving per login: a secondary login confirms the member's personal id
   * (partial NRIC + date of birth) so its profile, and so its QR, shows it. Asks the
   * MAIN API, the one that serves the profile, never the PIN API MyGiving uses (on
   * dev that one is staging). Resolves `{ ok, state }` or `{ ok: false, error }`;
   * never rejects. The caller refreshes the profile on success.
   */
  verifyPersonalId({ rootState }, { partialNric, dateOfBirth }) {
    return verifyPersonalIdAt({
      baseUrl: process.env.VUE_APP_API_URL,
      token: rootState.user.token,
      fields: { partialNric, dateOfBirth },
      logLabel: '[My QR]',
    });
  },

  async updateHasSeenUpdates({ commit, rootState }) {
    commit('setHasSeenUpdates', true);
    const accessToken = rootState.user.token;
    await axios.request(process.env.VUE_APP_API_URL + '/profile/update', {
      method: 'post',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      data: { hasSeenUpdates: true },
    });
  },
  inputRegisterForm({ commit, dispatch }, { key, value }) {
    commit('setRegisterFormState', { key, value });
    dispatch('validateRegisterForm', { key });
  },
  async getOnboardStatus(
    { commit, state, rootState },
    { useCache = true, backgroundSync = true } = {},
  ) {
    // Step 1: Load from localStorage first (non-blocking)
    if (useCache) {
      const cachedStatus = getOnboardingStatusFromStorage();
      if (cachedStatus !== null) {
        const statusValue = cachedStatus !== '' ? cachedStatus : null;
        commit('setOnboardStatus', {
          key: 'hasRegistered',
          value: statusValue,
        });
        commit('setRegisterFormState', {
          key: 'stage',
          value:
            statusValue !== null && statusValue !== undefined
              ? ONBOARD_STATUS[statusValue]
              : REGISTER_STAGE.FILL_BIO,
        });
        console.log(`[Profile] Loaded onboarding status from cache: ${statusValue}`);

        // If background sync is enabled, always sync in background (non-blocking)
        if (backgroundSync) {
          // Fire and forget - don't await
          this.dispatch('profile/syncOnboardStatusFromBackend', { silent: true }).catch(err => {
            console.warn('[Profile] Background sync of onboarding status failed:', err);
          });
        }
        return;
      }
    }

    // Step 2: If no cache or cache disabled, fetch from API
    try {
      const accessToken = rootState.user.token;
      const res = await axios.get(`${process.env.VUE_APP_API_URL}/v2/profile/onboarded-status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const statusValue = res.data !== null && res.data !== undefined ? res.data : null;

      // Save to localStorage
      saveOnboardingStatusToStorage(statusValue);

      commit('setOnboardStatus', {
        key: 'hasRegistered',
        value: statusValue,
      });
      commit('setRegisterFormState', {
        key: 'stage',
        value:
          statusValue !== null && statusValue !== undefined
            ? ONBOARD_STATUS[statusValue]
            : REGISTER_STAGE.FILL_BIO,
      });
    } catch (e) {
      // For 401 errors, throw to trigger global error handlers (which will logout user)
      if (e.response && e.response.status === 401) {
        throw e;
      }
      // For other errors (network, 500, CORS, timeout), log but don't throw, and
      // ASSUME REGISTERED.
      //
      // Swallowing alone did not achieve that, which is the bug this line fixes.
      // `hasRegistered` initialises to '' and was left there, and App.vue sets its
      // `onboardStatusSuccess` flag from whether this dispatch THREW — so a
      // swallowed failure looked like a success carrying a real answer of ''.
      // '' !== STAGE_3_LIFESTAGE_COMPLETED, so resolvePostGateNavigation sent a
      // fully-registered member to /register, on a page with no tab bar and no way
      // back. One flaky call to this endpoint was enough.
      //
      // Assuming registered is the safe direction: a genuinely unregistered member
      // is sent to /register by the next successful call, whereas a registered one
      // wrongly parked there cannot get out. Only ever assume it on FAILURE — never
      // overwrite a real answer, or the onboarding flow stops working.
      console.error('Error getting onboard status:', e);
      if (!state.hasRegistered) {
        commit('setOnboardStatus', {
          key: 'hasRegistered',
          value: ONBOARDING_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
        });
      }
    }
  },

  /**
   * Sync onboarding status from backend (used for background sync)
   * @param {Object} context - Vuex context
   * @param {Object} options - Options object
   * @param {boolean} options.silent - If true, don't log success messages
   */
  async syncOnboardStatusFromBackend({ commit, rootState }, { silent = false } = {}) {
    try {
      const accessToken = rootState.user.token;
      const res = await axios.get(`${process.env.VUE_APP_API_URL}/v2/profile/onboarded-status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const statusValue = res.data !== null && res.data !== undefined ? res.data : null;
      const currentStatus = getOnboardingStatusFromStorage();

      // Only update if value changed (or if no cached value exists)
      if (currentStatus === null || String(currentStatus) !== String(statusValue)) {
        saveOnboardingStatusToStorage(statusValue);
        commit('setOnboardStatus', {
          key: 'hasRegistered',
          value: statusValue,
        });
        commit('setRegisterFormState', {
          key: 'stage',
          value:
            statusValue !== null && statusValue !== undefined
              ? ONBOARD_STATUS[statusValue]
              : REGISTER_STAGE.FILL_BIO,
        });
        if (!silent) {
          console.log(`[Profile] Synced onboarding status from backend: ${statusValue}`);
        }
      } else if (!silent) {
        console.log('[Profile] Onboarding status already in sync with backend');
      }
    } catch (e) {
      // For 401 errors, throw to trigger global error handlers
      if (e.response && e.response.status === 401) {
        throw e;
      }
      // For other errors, log but don't throw (this is background sync)
      console.warn('[Profile] Background sync of onboarding status failed:', e);
    }
  },
  async getSchools({ state, commit, rootState }) {
    try {
      console.log('[Profile getSchools] Starting fetch. Current schools:', state.schools);
      if (state.schools) {
        console.log('[Profile getSchools] Schools already exist, skipping fetch');
        return;
      }
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/v2/profile/schools`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting schools');
      console.log('[Profile getSchools] Schools fetched successfully:', data);
      commit('setSchools', data);
      console.log('[Profile getSchools] Schools committed to state');
    } catch (e) {
      console.error('[Profile getSchools] Error:', e);
    }
  },
  async getLaunchBadgeEligibility({ commit, dispatch, rootState }) {
    try {
      const accessToken = rootState.user.token;
      const { status, data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/v2/profile/launch-badge-eligibility`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting launch badge eligibility');
      commit('setLaunchBadgeEligibility', data);
      return data;
    } catch (e) {
      console.log('Error getting launch badge eligibility:', e);
      dispatch('toast/error', 'Failed to check launch badge eligibility', {
        root: true,
      });
      throw e;
    }
  },
  blurField({ commit, dispatch }, key) {
    commit('setRegisterFormStateDirtied', { key });
    dispatch('validateRegisterForm', { key });
  },
  validateRegisterForm({ dispatch }, { key }) {
    // if (key === "email") dispatch("validateEmail");
    if (key === 'phone') dispatch('validateContactNumber');
    const fields = ['familyName', 'givenName'];
    if (fields.includes(key)) dispatch('validateRequiredFields', { key });
  },
  validateContactNumber({ commit, state }, isNumberValid) {
    if (!state.register.dirtied.has('phone')) return;
    if (!state.register.phone) {
      commit('setErrorMessages', {
        key: 'phone',
        messages: ['Required'],
      });
      return;
    }
    const messages = isNumberValid ? null : ['Enter a valid mobile number'];
    if (isNumberValid === undefined) return;
    commit('setErrorMessages', { key: 'phone', messages });
  },
  validateRequiredFields({ commit, state }, { key }) {
    if (!state.register.dirtied.has(key)) return;
    if (!state.register[key]) {
      commit('setErrorMessages', { key, messages: ['Required'] });
    } else {
      commit('setErrorMessages', { key, messages: null });
    }
  },
  async sendOtp({ commit, state, dispatch }) {
    commit('setLoading', true);
    try {
      const { data } = await this.axios.post('/profile/send-otp', {
        countryCode: state.register.countryCode,
        contactNumber: state.register.contactNumber,
      });
      if (data.status !== 200) throw new Error('Failed in sending otp');
    } catch (e) {
      if (
        e.response.data &&
        e.response.data.error &&
        e.response.data.error.details &&
        e.response.data.error.details.length
      ) {
        dispatch('toast/error', e.response.data.error.details[0].message, {
          root: true,
        });
      } else if (e.response.data && e.response.data.error) {
        dispatch('toast/error', e.response.data.error, { root: true });
      }
    } finally {
      commit('setLoading', false);
    }
  },
  async letsGo({ commit, state, dispatch }) {
    commit('setLoading', true);
    try {
      await this.axios.post('/profile/lets-go', {
        contactNumber: state.register.contactNumber,
        countryCode: state.register.countryCode,
        email: state.register.email,
      });

      commit('setRegisterFormState', {
        key: 'stage',
        value: REGISTER_STAGE.VERIFY_OTP,
      });
    } catch (e) {
      if (e.response.data && e.response.data.message)
        dispatch('toast/error', e.response.data.message, { root: true });
      else dispatch('toast/error', 'error in registration step', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },
  async verifyOtp({ commit, state, dispatch }, { otp }) {
    commit('setLoading', true);
    try {
      await this.axios.post('/profile/verify-otp', {
        contactNumber: state.register.contactNumber,
        countryCode: state.register.countryCode,
        otp,
      });
      commit('setRegisterFormState', {
        key: 'stage',
        value: REGISTER_STAGE.OTP_VERIFIED,
      });
    } catch (e) {
      if (e.response.data && e.response.data.message)
        dispatch('toast/error', e.response.data.message, { root: true });
      else dispatch('toast/error', 'otp verification failure', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },
  async completeRegistration({ commit, state, dispatch, rootState }) {
    commit('setLoading', true);

    try {
      const { familyName, givenName, contactNumber, password, email, countryCode } = state.register;
      const cgId = rootState.lookups.cgMapping[state.register.cg.toUpperCase()];
      await this.axios.post('/profile/register', {
        familyName,
        givenName,
        cg: cgId,
        contactNumber,
        password,
        email,
        countryCode,
        isIntegration: state.isIntegration,
      });
      commit('setRegisterFormState', {
        key: 'stage',
        value: REGISTER_STAGE.COMPLETED,
      });
      // Save onboarding status to localStorage after successful registration
      // Stage 3 (LIFESTAGE_COMPLETED) means onboarding is complete
      saveOnboardingStatusToStorage(ONBOARD_STATUS.STAGE_3_LIFESTAGE_COMPLETED);
      commit('setOnboardStatus', {
        key: 'hasRegistered',
        value: ONBOARD_STATUS.STAGE_3_LIFESTAGE_COMPLETED,
      });
    } catch (e) {
      let msg = e.message;
      if (e.response && e.response.data && e.response.data.message) {
        msg =
          e.response.data.message.length && Array.isArray(e.response.data.message)
            ? e.response.data.message[0]
            : e.response.data.message;
      }
      dispatch('toast/error', msg, { root: true });
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Update onboarding status after a stage completes
   * This should be called after successful POST/PUT to stage-1, stage-2, or stage-3 endpoints
   * @param {Object} context - Vuex context
   * @param {number} stage - The stage number (1, 2, or 3)
   */
  updateOnboardingStatusAfterStage({ commit }, stage) {
    let statusValue;
    switch (stage) {
      case 1:
        statusValue = ONBOARD_STATUS.STAGE_1_BIO_COMPLETED;
        break;
      case 2:
        statusValue = ONBOARD_STATUS.STAGE_2_CONTACT_COMPLETED;
        break;
      case 3:
        statusValue = ONBOARD_STATUS.STAGE_3_LIFESTAGE_COMPLETED;
        break;
      default:
        console.warn(`[Profile] Unknown onboarding stage: ${stage}`);
        return;
    }
    saveOnboardingStatusToStorage(statusValue);
    commit('setOnboardStatus', {
      key: 'hasRegistered',
      value: statusValue,
    });
    console.log(`[Profile] Updated onboarding status to stage ${stage} (${statusValue})`);
  },
  async requestChangePid({ commit, state, rootState }) {
    commit('setLoading', true);
    try {
      const token = rootState.user?.token;
      await axios.post(
        '/profile/pid-update-request',
        {
          dateOfBirth: state.dateOfBirth,
          partialNric: state.partialNric,
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
    } catch (e) {
      throw new Error(e);
    } finally {
      commit('setLoading', false);
    }
  },
};

const getters = {
  cgId(state) {
    return state.cgId;
  },
  cgName(state) {
    return state.cgName;
  },
  zoneName(state) {
    return state.zoneName;
  },
  age(state) {
    return new Date().getFullYear() - new Date(state.dateOfBirth).getFullYear();
  },
  fullName(state) {
    return state.givenName;
  },
  email(state) {
    return state.email;
  },
  encodedEmail(state) {
    return state.email;
  },
  localMobileNumber(state) {
    return state.mobileNumber;
  },
  countryCode(state) {
    return state.countryCode;
  },
  pastoralRoleId(state) {
    return state.roleId;
  },
  genderLongAbbr(state) {
    if (state.gender === 'M') return 'Male';
    if (state.gender === 'F') return 'Female';
    return null;
  },
  pastoralStatus(state) {
    return state.role;
  },
  levelOfStudy(state) {
    if (!state.schools) return [];
    return othersLast(
      Object.entries(state.schools).map(([key, value]) => ({
        value: value.id,
        label: key,
      })),
    );
  },
  schoolsByLevel: state => levelId => {
    console.log('[Profile schoolsByLevel] Called with levelId:', levelId);
    console.log('[Profile schoolsByLevel] state.schools keys:', Object.keys(state.schools || {}));
    if (!state.schools || !levelId) {
      console.log('[Profile schoolsByLevel] No schools or no levelId, returning []');
      return [];
    }

    const levelEntry = Object.entries(state.schools).find(([, level]) => level.id === levelId);
    console.log('[Profile schoolsByLevel] levelEntry:', levelEntry);
    if (!levelEntry) {
      console.log('[Profile schoolsByLevel] No levelEntry found for levelId:', levelId);
      return [];
    }

    const levelData = levelEntry[1];
    console.log('[Profile schoolsByLevel] levelData:', levelData);
    if (!levelData.schools) {
      console.log('[Profile schoolsByLevel] No schools in levelData');
      return [];
    }

    const schools = othersLast(
      levelData.schools.map(school => ({ value: school.id, label: school.name })),
    );
    console.log('[Profile schoolsByLevel] First 5 schools:', schools.slice(0, 5));
    console.log('[Profile schoolsByLevel] Returning total schools:', schools.length);
    return schools;
  },
  payload(state) {
    if (!state.givenName && !state.familyName) {
      return {
        fullName: undefined,
        familyName: undefined,
        preferredName: undefined,
        gender: undefined,
        partialNric: undefined,
        personalId: undefined,
        personalIdLocked: false,
        hasBioUpdateRequest: undefined,
        mobileNumber: undefined,
        email: undefined,
        stageOfLife: undefined,
        phaseId: undefined,
        maritalStatus: undefined,
        haveChildren: undefined,
        numberOfChildren: undefined,
        dateOfBirth,
        dateOfBirth_ddmm: undefined,
        countryCode: undefined,
        bioUpdateRequestFormId: undefined,
        cgUpdateRequestFormId: undefined,
        cgName: undefined,
        cgId: undefined,
        schoolId: undefined,
        schoolName: undefined,
        levelId: undefined,
        nationalityId: undefined,
        otherPhaseName: undefined,
        ...Object.fromEntries(CAREER_FIELD_KEYS.map(key => [key, undefined])),
      };
    }
    let dateOfBirth = dayjs(state.dateOfBirth).format('YYYY-MM-DD');
    if (dateOfBirth === 'Invalid Date') dateOfBirth = undefined; // to maintain falsy value if invalid date
    const payload = {
      fullName: state.givenName,
      familyName: state.familyName,
      preferredName: state.preferredName,
      gender: state.gender,
      partialNric: state.partialNric,
      personalId: state.personalId,
      personalIdLocked: state.personalIdLocked === true,
      hasBioUpdateRequest: state.hasBioUpdateRequest,
      isMobileNumberVerified: state.isMobileNumberVerified,
      mobileNumber: state.mobileNumber,
      countryCode: state.countryCode,
      email: state.email,
      stageOfLife: state.stageOfLife,
      phaseId: state.phaseId,
      maritalStatus: state.maritalStatus,
      haveChildren: state.haveChildren,
      numberOfChildren: state.numberOfChildren,
      dateOfBirth,
      dateOfBirth_ddmm: state.dateOfBirth_ddmm,
      bioUpdateRequestFormId: state.bioUpdateRequestFormId,
      cgUpdateRequestFormId: state.cgUpdateRequestFormId,
      cgName: state.cgName,
      cgId: state.cgId,
      schoolId: state.schoolId,
      schoolName: state.schoolName,
      levelId: state.levelId,
      nationalityId: state.nationalityId,
      otherPhaseName: state.otherPhaseName,
      ...Object.fromEntries(CAREER_FIELD_KEYS.map(key => [key, state[key] ?? null])),
    };
    console.log('[Profile payload getter] Your profile data from backend:', {
      stageOfLife: payload.stageOfLife,
      levelId: payload.levelId,
      schoolId: payload.schoolId,
      schoolName: payload.schoolName,
      fullName: payload.fullName,
    });
    return payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
